import React from 'react';
import { Outlet, Route, Routes, Navigate, useLocation } from 'react-router-dom';
import { PreLoginHeaderTrainingEnv } from 'components/Header/PreLoginHeaderTrainingEnv';
import { Masthead, Toaster } from 'ui';
import { PreLoginFooter } from 'components/Footer/PreLoginFooter';
import { useSelector } from 'react-redux';
import { getIsLoggedIn } from 'store/auth/selectors';
import { BroadcastBannerStack } from '../BroadcastBannerStack';
import { lazyWithRetry } from '../../utils/lazyWithRetry';
import { PageContentWrapper } from '../PageContentWrapper';
import { useTrainingEnvAuth } from 'auth/TrainingEnvAuthProvider';
import FeatureFlagTogglePage from 'pages/DevTools/FeatureFlagTogglePage';
import { useFeatureFlags } from 'feature-flags/useFeatureFlags';

const NotFoundPage = lazyWithRetry(() => import('pages/Errors/NotFoundPage'));
const LandingPage = lazyWithRetry(() => import('pages/LandingPage/LandingPage'));
const TermsOfUsePage = lazyWithRetry(() => import('pages/InfoPages/TermsOfUsePage/TermsOfUsePage'));
const PrivacyStatementPage = lazyWithRetry(() => import('pages/InfoPages/PrivacyStatementPage/PrivacyStatementPage'));
const ContactUsPage = lazyWithRetry(() => import('pages/InfoPages/ContactUsPage/ContactUsPage'));
const TrainingUsersDashboardPage = lazyWithRetry(() => import('pages/TrainingEnv/TrainingUsersDashboardPage'));
const TrainingLoginPage = lazyWithRetry(() => import('pages/TrainingEnv/TrainingEnvLoginPage'));
const TrainingRegisterPage = lazyWithRetry(() => import('pages/TrainingEnv/TrainingEnvRegisterPage'));

const TrainingPublicRouterWrapper: React.FC = () => {
  const { pathname } = useLocation();
  return (
    <PageContentWrapper>
      <div className="flex flex-col">
        <div className="flex flex-col w-full min-h-screen max-w-screen">
          <Masthead />
          <div className="sticky top-0 z-40">
            <PreLoginHeaderTrainingEnv showLoginButton={pathname !== '/login-with-otp'} />
          </div>
          <BroadcastBannerStack />
          <div className="flex flex-col flex-grow relative">
            <Outlet />
          </div>
          <div className="fixed top-20 right-4 z-50">
            <Toaster />
          </div>
        </div>
        <PreLoginFooter />
      </div>
    </PageContentWrapper>
  );
};

const TrainingPublicRouter: React.FC = () => {
  const { enableToggleFeatureFlagPanel } = useFeatureFlags();
  const isLoggedIn = useSelector(getIsLoggedIn);
  const { isAuthenticated } = useTrainingEnvAuth();

  return (
    <Routes>
      <Route element={<TrainingPublicRouterWrapper />}>
        <Route
          path="/"
          element={
            isLoggedIn ? (
              <Navigate to="auth/projects" replace />
            ) : isAuthenticated ? (
              <Navigate to="dashboard" replace />
            ) : (
              <LandingPage />
            )
          }
        />
        <Route
          path="/login-with-otp"
          element={isAuthenticated ? <Navigate to="../dashboard" /> : <TrainingLoginPage />}
        />
        <Route
          path="/register-with-otp"
          element={isAuthenticated ? <Navigate to="../dashboard" /> : <TrainingRegisterPage />}
        />
        <Route
          path="/dashboard"
          element={isAuthenticated ? <TrainingUsersDashboardPage /> : <Navigate to="../login-with-otp" />}
        />
        {enableToggleFeatureFlagPanel && <Route path="/toggle-flags" element={<FeatureFlagTogglePage />} />}
        <Route path="/terms-of-use" element={<TermsOfUsePage />} />
        <Route path="/privacy-statement" element={<PrivacyStatementPage />} />
        <Route path="/contact-us" element={<ContactUsPage />} />
        <Route path="/*" element={<NotFoundPage isLoggedIn={false} />} />
      </Route>
    </Routes>
  );
};

export default TrainingPublicRouter;
